/**
 * CutscenePlayer.tsx — WEB.4.CS
 *
 * Full-screen cinematic overlay that walks through a sequence of frames
 * (Tier 1 intro / Tier 2 era transitions / Tier 3 endings). Each frame has
 * an image (`/cutscenes/{id}.jpg`), narration text, and a manual NEXT button.
 *
 * Frame art is NOT auto-advanced — the player clicks NEXT (or presses Space /
 * Enter) to proceed. This prevents the "frame advances before art loads" bug
 * (R10 #9). The durationMs field is now used only as a visual progress hint.
 *
 * Preload: when frameIdx changes, the player eagerly fetches frameIdx+1 and
 * frameIdx+2 via Image() so the next click is instant.
 *
 * Skip controls:
 *   - SKIP → button (top-right): drops the entire sequence
 *   - NEXT ▶ / DONE ✓ button (bottom-right): manual advance / close
 *   - Space / Enter: same as NEXT ▶
 *   - Esc: same as NEXT ▶ (kept for fast keyboard users)
 *   - showCutscenes=false: never renders (settings toggle)
 *
 * VISIBLE WHEN: store.cutsceneGroupId !== null AND showCutscenes === true.
 */

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BGMKey } from "../../audio/audio.js";
import { bgmKeyForEraRound, crossfadeBGM, stopBGM } from "../../audio/audio.js";
import {
  type CutsceneGroupId,
  getCutsceneFrame,
  getCutsceneGroup,
  paletteForGroup,
} from "../../cutscene/cutscene-data.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./CutscenePlayer.module.css";

/** Eagerly preload an imgSrc URL via the browser's image cache. */
function preloadImage(src: string): void {
  const img = new Image();
  img.src = src;
}

/** Look up the imgSrc for a given (groupId, frameIndex). Returns null if OOB. */
function getFrameImgSrc(groupId: CutsceneGroupId, idx: number): string | null {
  const group = getCutsceneGroup(groupId);
  if (group === undefined) return null;
  const id = group.frameIds[idx];
  if (id === undefined) return null;
  return getCutsceneFrame(id)?.imgSrc ?? null;
}

export function CutscenePlayer(): React.ReactElement | null {
  const groupId = useGameStore((s) => s.cutsceneGroupId);
  const frameIdx = useGameStore((s) => s.cutsceneFrameIdx);
  const showCutscenes = useGameStore((s) => s.showCutscenes);
  const audioMuted = useGameStore((s) => s.audioMuted);
  const advance = useGameStore((s) => s.advanceCutsceneFrame);
  const skip = useGameStore((s) => s.skipCutscene);

  const [imgFailed, setImgFailed] = useState(false);
  // imgLoaded: false until the current frame's <img> fires onLoad. While false
  // we render the era-palette gradient placeholder UNDER the image so a slow
  // fetch shows the cinematic placeholder instead of a blank black frame. This
  // is the slow-load counterpart to imgFailed (which only fires on a 404).
  const [imgLoaded, setImgLoaded] = useState(false);
  const skipBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);

  const group = useMemo(() => {
    if (groupId === null) return null;
    return getCutsceneGroup(groupId) ?? null;
  }, [groupId]);

  const frame = useMemo(() => {
    if (group === null) return null;
    const id = group.frameIds[frameIdx];
    if (id === undefined) return null;
    return getCutsceneFrame(id) ?? null;
  }, [group, frameIdx]);

  const totalFrames = group?.frameIds.length ?? 0;
  const isFinalFrame = frameIdx === totalFrames - 1;

  // Reset img-failed flag whenever the frame changes so each new frame gets
  // a fresh load attempt. codex R1 MED: deps were [] (mount-only) — one 404
  // would poison every subsequent frame into fallback. The biome-ignore is
  // needed because `setImgFailed` (the only deps biome can see) is stable,
  // but we semantically depend on `frame?.id` to fire on frame change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: frame.id is the intended trigger
  useEffect(() => {
    setImgFailed(false);
    // Reset load state too: each new frame starts as "not yet loaded" so the
    // gradient placeholder shows until this frame's <img> onLoad fires. If the
    // image is already in the browser cache, onLoad fires synchronously on the
    // next paint, so warm frames flash no placeholder.
    setImgLoaded(false);
  }, [frame?.id]);

  // Preload next 2 frames whenever frameIdx changes (R10 #9B).
  // This fires eagerly so by the time the player clicks NEXT the browser
  // already has the image in cache — zero white-flash delay.
  useEffect(() => {
    if (groupId === null) return;
    const src1 = getFrameImgSrc(groupId, frameIdx + 1);
    const src2 = getFrameImgSrc(groupId, frameIdx + 2);
    if (src1) preloadImage(src1);
    if (src2) preloadImage(src2);
  }, [groupId, frameIdx]);

  // Keyboard: Space / Enter / Esc advance one frame (R10 #9A).
  // Capture phase so we run before PauseMenu's Esc handler.
  useEffect(() => {
    if (groupId === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" || e.key === " " || e.code === "Space" || e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        advance();
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [groupId, advance]);

  // Focus NEXT button when cutscene opens / frame changes so keyboard users
  // can press Space/Enter immediately without an extra Tab.
  // biome-ignore lint/correctness/useExhaustiveDependencies: frameIdx intentional dep for re-focus per frame
  useEffect(() => {
    if (groupId === null) return;
    const id = window.setTimeout(() => {
      nextBtnRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [groupId, frameIdx]);

  // R11.2.5 fix #11: per-group BGM routing.
  // intro / transition-1/2/3 / ending-A/B/C/D map to their own tracks.
  // R11.2.5 fix #10: on cleanup, resume era BGM if game is still playing —
  // without this, the intro → game-start path leaves the gameboard silent
  // because HexBoard's era/round useEffect doesn't fire (era unchanged).
  useEffect(() => {
    if (groupId === null || audioMuted) return;
    const bgmKey: BGMKey = (() => {
      if (groupId === "intro") return "cutscene-intro";
      if (groupId === "transition-1") return "cutscene-transition-1";
      if (groupId === "transition-2") return "cutscene-transition-2";
      if (groupId === "transition-3") return "cutscene-transition-2"; // no -3 dropped yet — reuse
      if (groupId === "ending-A") return "ending-a";
      if (groupId === "ending-B") return "ending-b";
      if (groupId === "ending-C") return "ending-c";
      if (groupId === "ending-D") return "ending-d";
      return "cutscene-intro";
    })();
    crossfadeBGM(bgmKey, 800);
    return () => {
      // Resume era BGM after intro / transition cutscenes. Endings stay
      // silent (game has ended; GameOverModal owns the audio beat).
      if (groupId.startsWith("ending")) {
        stopBGM(600);
        return;
      }
      const s = useGameStore.getState();
      if (s.audioMuted) {
        stopBGM(600);
        return;
      }
      if (s.gamePhase === "playing") {
        crossfadeBGM(bgmKeyForEraRound(s.era, s.round), 600);
      } else {
        stopBGM(600);
      }
    };
  }, [groupId, audioMuted]);

  if (!showCutscenes) return null;
  if (groupId === null) return null;
  if (frame === null) return null;

  const palette = paletteForGroup(groupId);

  return (
    <dialog
      open
      className={styles.overlay}
      data-testid="cutscene-overlay"
      aria-labelledby="cutscene-narration"
      style={{ background: palette.bg }}
    >
      {/* Image layer — gradient placeholder shows on 404 (imgFailed) AND while
          the image is still loading (slow-fetch guard). The <img> fades in on
          top once onLoad fires; a slow iPad therefore never shows a blank
          black frame, only the cinematic gradient until the art arrives. */}
      <div className={styles.imageLayer} aria-hidden="true">
        {/* Gradient placeholder — rendered whenever the image isn't shown yet
            (failed OR not-yet-loaded). data-testid distinguishes the two so
            tests can assert the 404 fallback path specifically. */}
        {(imgFailed || !imgLoaded) && (
          <div
            className={styles.imagePlaceholder}
            data-testid={imgFailed ? "cutscene-image-fallback" : "cutscene-image-placeholder"}
            style={{
              background: `radial-gradient(ellipse at center, ${palette.fg}33 0%, ${palette.bg} 70%)`,
            }}
          />
        )}
        {!imgFailed && (
          <img
            src={frame.imgSrc}
            alt={frame.title}
            className={`${styles.image}${imgLoaded ? ` ${styles.imageReady}` : ""}`}
            data-testid="cutscene-image"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        )}
      </div>

      {/* Letterbox bars to enforce cinematic 3:2 framing on any viewport. */}
      <div className={styles.letterboxTop} aria-hidden="true" />
      <div className={styles.letterboxBottom} aria-hidden="true" />

      {/* Narration caption — primary readable element. */}
      <div
        id="cutscene-narration"
        className={styles.narration}
        data-testid="cutscene-narration"
        style={{ color: palette.fg }}
      >
        {frame.narration}
      </div>

      {/* Skip button — top-right. Drops the entire sequence. */}
      <button
        ref={skipBtnRef}
        type="button"
        className={styles.skipBtn}
        onClick={() => skip()}
        data-testid="cutscene-skip"
        aria-label="Skip cutscene"
      >
        SKIP &nbsp;→
      </button>

      {/* NEXT / DONE button — bottom-right (R10 #9A manual advance). */}
      <button
        ref={nextBtnRef}
        type="button"
        className={isFinalFrame ? styles.doneBtn : styles.nextBtn}
        onClick={() => advance()}
        data-testid={isFinalFrame ? "cutscene-done" : "cutscene-next"}
        aria-label={isFinalFrame ? "Close cutscene" : "Next frame"}
      >
        {isFinalFrame ? "DONE ✓" : "NEXT ▶"}
      </button>

      {/* Frame index pill — bottom-right corner inside letterbox. */}
      <div className={styles.frameIndex} aria-hidden="true">
        {frameIdx + 1} / {totalFrames || "?"}
      </div>
    </dialog>
  );
}
